import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useDataProvider, useLocalization } from '@ui-framework/index';
import { toLongDate } from '@utils/Dated';
import { buildNotificationGroupApiPath } from '../../apiPaths';
import { getNotificationTypeLabel, unreadCountOf } from '../utils';

// Keeps text in the accessible name without rendering it visually.
const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
};

const groupIdentity = notification =>
  notification.groupSize > 1 && notification.sourceEntityId
    ? JSON.stringify([
        notification.sourceModule,
        notification.sourceEntityType,
        notification.sourceEntityId,
      ])
    : notification.id;

function NotificationListItems({
  notifications,
  loading,
  onSelect,
  emptyMessage,
}) {
  const { formatMessage } = useLocalization();
  const provider = useDataProvider(
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default',
  );
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [membersById, setMembersById] = useState({});
  const [loadingIds, setLoadingIds] = useState(() => new Set());
  const [failedIds, setFailedIds] = useState(() => new Set());
  const membersVersionRef = useRef(0);
  const membersControllerRef = useRef(null);

  const expandedIdsRef = useRef(expandedIds);
  expandedIdsRef.current = expandedIds;

  const invalidateGroupMembers = notificationId => {
    setMembersById(current => {
      if (!current[notificationId]) {
        return current;
      }
      const next = { ...current };
      delete next[notificationId];
      return next;
    });
  };

  const loadGroup = useCallback(
    async (notification, preserveExpansion = false) => {
      const notificationId = groupIdentity(notification);
      setLoadingIds(current => new Set(current).add(notificationId));
      setFailedIds(current => {
        const next = new Set(current);
        next.delete(notificationId);
        return next;
      });
      const membersVersion = membersVersionRef.current;
      try {
        const result = await provider.get(
          buildNotificationGroupApiPath(notification.id),
          null,
          {
            suppressGlobalErrorRedirect: true,
            allowTokenRenewal: true,
            signal: membersControllerRef.current?.signal,
          },
        );
        const members = result?.ok
          ? (result?.data?.data ?? result?.data)
          : null;
        if (!Array.isArray(members)) {
          throw new Error('Notification group request failed');
        }
        if (membersVersion === membersVersionRef.current) {
          setMembersById(current => ({
            ...current,
            [notificationId]: [...members].sort((left, right) => {
              const creationOrder =
                Number(right.notificationTypeCode === 'ISSUE_CREATED') -
                Number(left.notificationTypeCode === 'ISSUE_CREATED');
              return (
                creationOrder ||
                (Date.parse(left.createdDateTime) || 0) -
                  (Date.parse(right.createdDateTime) || 0)
              );
            }),
          }));
        }
      } catch {
        if (membersVersion === membersVersionRef.current) {
          if (preserveExpansion) {
            setFailedIds(current => new Set(current).add(notificationId));
          } else {
            setExpandedIds(current => {
              const next = new Set(current);
              next.delete(notificationId);
              return next;
            });
          }
        }
      } finally {
        if (membersVersion === membersVersionRef.current) {
          setLoadingIds(current => {
            const next = new Set(current);
            next.delete(notificationId);
            return next;
          });
        }
      }
    },
    [provider],
  );

  useEffect(() => {
    const controller = new AbortController();
    membersControllerRef.current = controller;
    membersVersionRef.current += 1;
    const expandedGroups = (notifications ?? []).filter(
      notification =>
        notification.groupSize > 1 &&
        expandedIdsRef.current.has(groupIdentity(notification)),
    );
    setExpandedIds(new Set(expandedGroups.map(groupIdentity)));
    setMembersById({});
    setLoadingIds(new Set());
    setFailedIds(new Set());
    expandedGroups.forEach(notification => loadGroup(notification, true));
    return () => {
      membersVersionRef.current += 1;
      controller.abort();
    };
  }, [notifications, loadGroup]);

  const toggleGroup = notification => {
    const notificationId = groupIdentity(notification);
    if (expandedIds.has(notificationId)) {
      setExpandedIds(current => {
        const next = new Set(current);
        next.delete(notificationId);
        return next;
      });
      return;
    }
    setExpandedIds(current => new Set(current).add(notificationId));
    if (!membersById[notificationId] && !loadingIds.has(notificationId)) {
      loadGroup(notification);
    }
  };

  if (loading) {
    return (
      <ListItem sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </ListItem>
    );
  }
  if (!notifications?.length) {
    return (
      <ListItem sx={{ p: 3, justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {emptyMessage ??
            formatMessage({
              id: 'YOU_HAVE_NO_NOTIFICATIONS',
              defaultMessage: 'You have no notifications',
            })}
        </Typography>
      </ListItem>
    );
  }

  const isUnread = notification => unreadCountOf(notification) > 0;
  const selectNotification = notification => {
    if (notification.groupSize > 1 && isUnread(notification)) {
      invalidateGroupMembers(groupIdentity(notification));
    }
    onSelect(notification);
  };

  return notifications.map(notification => {
    const notificationId = groupIdentity(notification);
    const expanded = expandedIds.has(notificationId);
    const members = membersById[notificationId];
    const summary = expanded && members?.length ? members[0] : notification;
    return (
      <ListItem
        key={notificationId}
        component="li"
        disablePadding
        alignItems="flex-start"
        sx={{
          display: 'block',
          borderLeft: theme =>
            isUnread(notification)
              ? `3px solid ${theme.palette.primary.main}`
              : '3px solid transparent',
        }}>
        <ListItemButton
          onClick={() => selectNotification(notification)}
          sx={{ alignItems: 'flex-start' }}>
          <ListItemText
            sx={{ overflowWrap: 'anywhere' }}
            secondaryTypographyProps={{ component: 'div' }}
            primary={
              <Typography
                variant="body2"
                sx={{ fontWeight: isUnread(notification) ? 600 : 400 }}>
                {summary.title}
                {isUnread(notification) && (
                  <Box component="span" sx={visuallyHidden}>
                    {' '}
                    {formatMessage({
                      id: 'UNREAD',
                      defaultMessage: 'Unread',
                    })}
                  </Box>
                )}
              </Typography>
            }
            secondary={
              <>
                <Typography
                  component="span"
                  variant="caption"
                  display="block"
                  color="text.secondary">
                  {summary.message}
                </Typography>
                <Typography
                  component="span"
                  variant="caption"
                  display="block"
                  color="text.disabled">
                  {getNotificationTypeLabel(formatMessage, summary)}
                  {summary.createdDateTime
                    ? ` · ${toLongDate(summary.createdDateTime)}`
                    : ''}
                </Typography>
              </>
            }
          />
        </ListItemButton>
        {notification.groupSize > 1 && (
          <>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ px: 2, overflowWrap: 'anywhere' }}>
                {loadingIds.has(notificationId) && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={16} />
                  </Box>
                )}
                {failedIds.has(notificationId) && (
                  <Typography
                    role="status"
                    variant="caption"
                    color="text.secondary">
                    {formatMessage({
                      id: 'COULDNT_LOAD_NOTIFICATIONS_TRY_AGAIN_LATER',
                      defaultMessage:
                        "Couldn't load notifications. Try again later.",
                    })}
                  </Typography>
                )}
                {members?.slice(1).map(member => (
                  <Box
                    key={member.id}
                    sx={{
                      pl: 1.5,
                      py: 0.5,
                      borderLeft: theme => `2px solid ${theme.palette.divider}`,
                      mb: 0.5,
                    }}>
                    <Typography
                      component="span"
                      variant="caption"
                      display="block"
                      color="text.secondary">
                      {member.message ?? member.title}
                    </Typography>
                    <Typography
                      component="span"
                      variant="caption"
                      display="block"
                      color="text.disabled">
                      {getNotificationTypeLabel(formatMessage, member)}
                      {member.createdDateTime
                        ? ` · ${toLongDate(member.createdDateTime)}`
                        : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
            <Box sx={{ px: 2, pb: 1 }}>
              <Button
                size="small"
                color="primary"
                aria-expanded={expanded}
                onClick={event => {
                  event.stopPropagation();
                  toggleGroup(notification);
                }}>
                {expanded
                  ? formatMessage({
                      id: 'SHOW_LESS',
                      defaultMessage: 'Show less',
                    })
                  : formatMessage(
                      {
                        id: 'NOTIFICATIONS_GROUP_MORE_UPDATES',
                        defaultMessage:
                          '{count, plural, one {# more update} other {# more updates}}',
                      },
                      { count: notification.groupSize - 1 },
                    )}
              </Button>
            </Box>
          </>
        )}
      </ListItem>
    );
  });
}

export default NotificationListItems;
